import type { HotelAvgRoomNight } from '@travel-planner/shared/schemas';
import {
  AVG_ROOM_NIGHT_MIN_SAMPLE_SIZE,
  avgRoomNightTooltip,
  AVG_ROOM_NIGHT_DISCLAIMER as DISCLAIMER,
  usableAvgRoomNight,
} from '@travel-planner/shared/utils/hotel-avg-room-night';
import { Tooltip } from '../tooltip';
import css from './avg-room-night-price.module.scss';

/** Re-exported from shared, where the portal's Midlayer reads them too. */
export const AVG_ROOM_NIGHT_DISCLAIMER = DISCLAIMER;
export const MIN_SAMPLE_SIZE = AVG_ROOM_NIGHT_MIN_SAMPLE_SIZE;

export type AvgRoomNightPriceProps = {
  avgRoomNight: HotelAvgRoomNight | null | undefined;
  /** Rendered before the amount. Defaults to `Avg`. */
  label?: string;
};

const formatEur = (amount: number): string => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return amount.toLocaleString('en-US');
  }
};

/**
 * A hotel's average client SELL PRICE per room, per night, drawn from past sold
 * itineraries — always with the disclaimer on hover.
 *
 * Renders nothing when there is no roll-up yet, when the average is not a
 * positive number, or when too few bookings fed it (`MIN_SAMPLE_SIZE`). Showing
 * nothing is the correct outcome for a new hotel: an empty slot reads as "no
 * history", where `€0` would read as "free".
 *
 * The figure is deliberately labelled and set apart from the product's own
 * catalog price, which sits beside it on the same card and means something
 * different (what the client pays, not what the hotel cost).
 */
export const AvgRoomNightPrice = ({
  avgRoomNight,
  label = 'Avg',
}: AvgRoomNightPriceProps) => {
  const avgSellPriceEur = usableAvgRoomNight(avgRoomNight);
  if (avgRoomNight == null || avgSellPriceEur == null) return null;
  const amount = formatEur(avgSellPriceEur);

  return (
    <Tooltip
      bubbleClassName={css.bubble}
      className={css.root}
      content={avgRoomNightTooltip(avgRoomNight.sampleSize)}
    >
      <span className={css.amount}>
        {label ? `${label} ${amount}` : amount}
      </span>
      <span>/ night</span>
    </Tooltip>
  );
};
